import {
  createContext,
  useContext,
  useState,
  useCallback,
} from "react";
import { useQuery } from "@tanstack/react-query";

import axiosClient from "../Api/axiosClient";
import { qk } from "../Api/queryClient";

import { fetchDepartments } from "../Api/ViewDepartment";
import { fetchMerchants } from "../Api/ViewMerchantMaster";
import { fetchAllResources } from "../Api/ResourceMaster";
import { fetchItems } from "../Api/ViewItems";
import { fetchRoles } from "../Api/RoleMaster";
import { fetchHSNCodes, fetchTaxCodes } from "../Api/IncomingPo";
import { fetchNodes } from "../Api/NodesApi";

const MasterDataContext = createContext(null);

const FIVE_MIN = 5 * 60 * 1000;

const fetchList = (url, params) =>
  axiosClient.get(url, { params }).then((r) => r.data?.data ?? []);

export const MasterDataProvider = ({ children }) => {
  // ==========================================
  // Existing Master Data State
  // ==========================================
  const [departmentsData, setDepartmentsData] = useState([]);
  const [merchantsData, setMerchantsData] = useState([]);
  const [resourceData, setResourceData] = useState([]);
  const [itemData, setItemData] = useState([]);
  const [sideBarData, setSideBarData] = useState([]);
  const [roleData, setRoleData] = useState([]);
  const [hsnCodes, setHsnCodes] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);

  // ==========================================
  // Existing Load Functions
  // ==========================================
  const loadDepartments = async () => {
    try {
      const data = await fetchDepartments();
      setDepartmentsData(data);
    } catch (error) {
      console.error("Failed to fetch departments:", error);
    }
  };

  const loadMerchants = async () => {
    try {
      const data = await fetchMerchants();
      setMerchantsData(data);
    } catch (error) {
      console.error("Failed to fetch merchants:", error);
    }
  };

  const loadResources = async () => {
    try {
      const data = await fetchAllResources();
      setResourceData(data);
    } catch (error) {
      console.error("Failed to fetch resources:", error);
    }
  };

  const loadItems = async () => {
    try {
      const data = await fetchItems();
      setItemData(data);
    } catch (error) {
      console.error("Failed to fetch items:", error);
    }
  };

  const loadSideBars = useCallback(async () => {
    try {
      const data = await fetchNodes();
      setSideBarData(data);
    } catch (error) {
      console.error("Failed to fetch sidebar data:", error);
    }
  }, []);

  const loadRoleMaster = async () => {
    try {
      const data = await fetchRoles();

      const formattedRoles =
        data?.map((role) => ({
          label: role?.description,
          value: role?.code,
        })) ?? [];

      setRoleData(formattedRoles);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    }
  };

  const loadHSNCodes = async () => {
    try {
      const data = await fetchHSNCodes();
      setHsnCodes(data);
    } catch (error) {
      console.error("Failed to fetch HSN codes:", error);
    }
  };

  const loadTaxCodes = async () => {
    try {
      const data = await fetchTaxCodes();
      setTaxCodes(data);
    } catch (error) {
      console.error("Failed to fetch Tax codes:", error);
    }
  };

  // ==========================================
  // React Query Lookup Hooks
  // ==========================================
  const useCountries = () =>
    useQuery({
      queryKey: qk.lookup("countries"),
      queryFn: () => fetchList("/lookups/countries"),
      staleTime: FIVE_MIN,
    });

  const useStations = () =>
    useQuery({
      queryKey: qk.lookup("stations"),
      queryFn: () => fetchList("/lookups/stations"),
      staleTime: FIVE_MIN,
    });

  const useMyStations = () =>
    useQuery({
      queryKey: qk.lookup("my-stations"),
      queryFn: () => fetchList("/lookups/my-stations"),
      staleTime: FIVE_MIN,
    });

  const useGeoByPincode = (pincode) =>
    useQuery({
      queryKey: qk.lookup("geo-by-pincode", pincode),
      queryFn: () =>
        axiosClient
          .get("/lookups/geo-by-pincode", {
            params: { pincode },
          })
          .then((r) => r.data?.data),
      enabled: !!pincode && String(pincode).trim().length >= 4,
      staleTime: FIVE_MIN,
    });

  const useStates = (countryCode) =>
    useQuery({
      queryKey: qk.lookup("states", countryCode),
      queryFn: () =>
        fetchList("/lookups/states", {
          countryCode,
        }),
      enabled: !!countryCode,
      staleTime: FIVE_MIN,
    });

  const useDistricts = (countryCode, regionCode) =>
    useQuery({
      queryKey: qk.lookup(
        "districts",
        countryCode,
        regionCode
      ),
      queryFn: () =>
        fetchList("/lookups/districts", {
          countryCode,
          regionCode,
        }),
      enabled: !!regionCode,
      staleTime: FIVE_MIN,
    });

  const useTehsils = (districtCode, districtName) =>
    useQuery({
      queryKey: qk.lookup(
        "tehsils",
        districtCode,
        districtName
      ),
      queryFn: () =>
        fetchList("/lookups/tehsils", {
          districtCode,
          districtName,
        }),
      enabled: !!districtCode,
      staleTime: FIVE_MIN,
    });

  const useCities = (tehsilCode, tehsilName) =>
    useQuery({
      queryKey: qk.lookup(
        "cities",
        tehsilCode,
        tehsilName
      ),
      queryFn: () =>
        fetchList("/lookups/cities", {
          tehsilCode,
          tehsilName,
        }),
      enabled: !!tehsilCode,
      staleTime: FIVE_MIN,
    });

  const usePincodes = (cityGuid) =>
    useQuery({
      queryKey: qk.lookup("pincodes", cityGuid),
      queryFn: () =>
        fetchList("/lookups/pincodes", {
          cityGuid,
        }),
      enabled: !!cityGuid,
      staleTime: FIVE_MIN,
    });

  const value = {
    // Existing State Data
    departmentsData,
    merchantsData,
    resourceData,
    itemData,
    sideBarData,
    roleData,
    hsnCodes,
    taxCodes,

    // Existing Load Methods
    loadDepartments,
    loadMerchants,
    loadResources,
    loadItems,
    loadSideBars,
    loadRoleMaster,
    loadHSNCodes,
    loadTaxCodes,

    // React Query Hooks
    useCountries,
    useStations,
    useMyStations,
    useGeoByPincode,
    useStates,
    useDistricts,
    useTehsils,
    useCities,
    usePincodes,
  };

  return (
    <MasterDataContext.Provider value={value}>
      {children}
    </MasterDataContext.Provider>
  );
};

export const useMasterData = () => {
  const ctx = useContext(MasterDataContext);

  if (!ctx) {
    throw new Error(
      "useMasterData must be used within MasterDataProvider"
    );
  }

  return ctx;
};

export const useMasterDataContext = useMasterData;